// @flow
import { useReducer, useEffect, useRef } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import type { Page, Category } from '~/app/types';
import type { $Slot as Slot } from 'shared/templates/types';
import { canRedo, canUndo, hasLocalEdits, historyStep, withHistory } from './draftHistory';

export type DraftState = {
  draft: Page,
  activeRevision: ?string,
}

/**
 * Carry the current draft's component `_id`s onto freshly-fetched components of
 * the same page, matched by position + widgetId (recursing into subcomponents).
 *
 * Why: the server regenerates every component `_id` on each save (component ids
 * aren't part of the page input contract, so Mongoose mints new subdocument ids
 * every time). The editor uses `key={component._id}` for each rendered widget,
 * so without this, the post-save refetch would change every key and React would
 * unmount + remount the entire page content — which collapses the scroll
 * container and jumps it to the top. Preserving the prior ids keeps the keys
 * stable so a save re-renders in place instead of remounting.
 *
 * Only used when reconciling the SAME page (guarded by caller); on a widgetId
 * mismatch (structural change) we fall back to the incoming id for that branch.
 */
const reconcileComponentIds = (oldComponents, newComponents) => {
  if (!Array.isArray(newComponents)) return newComponents;
  const prev = Array.isArray(oldComponents) ? oldComponents : [];

  return newComponents.map((newComp, i) => {
    const oldComp = prev[i];
    // Containers can have empty spots (null), in either version.
    if (!oldComp || !newComp || oldComp.widgetId !== newComp.widgetId) {
      return newComp;
    }

    const merged = { ...newComp, _id: oldComp._id };
    if (Array.isArray(newComp.subcomponents)) {
      merged.subcomponents = reconcileComponentIds(oldComp.subcomponents, newComp.subcomponents);
    }
    return merged;
  });
};

export const draftReducer = (state: DraftState, action) => {
  switch (action.type) {
    case 'replace_draft': {
      const incoming = action.payload;
      const current = state.draft;

      // When refetching the SAME page (e.g. after a save), keep the existing
      // component ids so React keys stay stable and the content doesn't remount
      // + scroll to the top. Different page or missing slots → use incoming as-is.
      if (
        current && incoming &&
        current._id === incoming._id &&
        Array.isArray(current.slots) && Array.isArray(incoming.slots)
      ) {
        const slots = incoming.slots.map((newSlot) => {
          const oldSlot = current.slots.find(s => s.slotId === newSlot.slotId);
          if (!oldSlot) return newSlot;
          return {
            ...newSlot,
            components: reconcileComponentIds(oldSlot.components, newSlot.components)
          };
        });

        return {
          ...state,
          activeRevision: null,
          draft: { ...incoming, slots },
        };
      }

      return {
        ...state,
        activeRevision: null,
        draft: incoming,
      };
    }
    case 'patch_draft_meta': {
      return {
        ...state,
        draft: {
          ...state.draft,
          updatedAt: action.payload.updatedAt,
        },
      };
    }
    case 'replace_version': {
      const version = action.payload;
      const { versionId: activeRevision, slots, template } = version;

      return {
        ...state,
        activeRevision,
        draft: {
          ...state.draft,
          slots,
          template
        }
      };
    }
    case 'reset_active_revision': {
      return {
        ...state,
        activeRevision: null
      };
    }
    case 'update_slot': {
      const slot = action.payload;

      return {
        ...state,
        draft: {
          ...state.draft,
          slots: [
            ...(state.draft.slots.filter(s => s.slotId !== slot.slotId)),
            slot
          ]
        },
      };
    }
    case 'change_version': {
      const version = action.payload;

      return {
        ...state,
        draft: {
          ...state.draft,
          template: {
            ...state.draft.template,
            version
          }
        }
      };
    }
    case 'change_template_settings': {
      const settings = action.payload;

      return {
        ...state,
        draft: {
          ...state.draft,
          template: {
            ...state.draft.template,
            props: settings
          }
        }
      };
    }
    case 'change_page_settings': {
      const { key, value } = action.payload;

      if (key === 'path') {
        if (value.search(/^[a-zA-Z0-9_-]*$/) > -1) {
          // Assembly the full path here.
          const categoryPath = state.draft.path.split('/').slice(0, -1).join('/');

          return {
            ...state,
            draft: {
              ...state.draft,
              path: `${categoryPath}/${value}`
            }
          };
        }
      } else if (key.indexOf('meta-') > -1) {
        const metadata = cloneDeep(state.draft.metadata);
        const index = metadata.findIndex(meta => meta.key === key.slice(5));

        metadata[index] = {
          key: key.slice(5),
          value
        };

        return {
          ...state,
          draft: {
            ...state.draft,
            metadata
          }
        };
      } else {
        return {
          ...state,
          draft: {
            ...state.draft,
            [key]: value
          }
        };
      }

      return { ...state };
    }
    case 'change_category': {
      const category = action.payload;

      // Recreate path with the new category
      const originalPath = state.draft.path.split('/').slice(-1);

      return {
        ...state,
        draft: {
          ...state.draft,
          category: {
            ...state.draft.category,
            ...category
          },
          path: `${category.path}/${originalPath}`
        }
      };
    }
    default:
      throw new Error('Invalid draft update');
  }
};

const historyReducer = withHistory(draftReducer);

const useEditorDraft = (activePage: Page, previewVersion: ?Object) => {
  const [draftState = {}, dispatchDraftUpdate] = useReducer(historyReducer, {
    draft: activePage,
    activeRevision: null,
    past: [],
    future: []
  });

  const stateRef = useRef(draftState);
  stateRef.current = draftState;
  // Set before an action that should replace local edits with the server's copy (restoring a version).
  const forceAdoptRef = useRef(false);

  // Sync draft from page data or preview version
  useEffect(() => {
    if (!activePage) return;
    const current = stateRef.current;
    if (previewVersion) {
      // A version only has slots and template: lay it over this page. When the
      // version loads before the page, the draft isn't this page yet, so take
      // the page first (otherwise the draft has no path, title or category).
      if (!current.draft || current.draft._id !== activePage._id) {
        dispatchDraftUpdate({ type: 'replace_draft', payload: activePage });
      }
      dispatchDraftUpdate({ type: 'replace_version', payload: previewVersion });
      return;
    }
    const samePage = current.draft && current.draft._id === activePage._id;
    if (samePage && hasLocalEdits(current) && !forceAdoptRef.current) {
      // The server copy changed (usually our own save landing) while there are
      // newer local edits: keep them, but take the server's timestamp so the
      // next save's "outdated" check stays right.
      dispatchDraftUpdate({ type: 'patch_draft_meta', payload: { updatedAt: activePage.updatedAt } });
      return;
    }
    forceAdoptRef.current = false;
    dispatchDraftUpdate({ type: 'replace_draft', payload: activePage });
  }, [activePage, previewVersion]);

  const { draft, activeRevision } = draftState;

  // `coalesce` names a field being typed in, so a burst of edits to it is one undo step.
  const updateSlot = (slot: Slot, coalesce?: string) => {
    dispatchDraftUpdate({ type: 'update_slot', payload: slot, coalesce });
  };

  const changeVersion = (version: string) => {
    dispatchDraftUpdate({ type: 'change_version', payload: version });
  };

  const changeTemplateSettings = (settings: Object[]) => {
    dispatchDraftUpdate({ type: 'change_template_settings', payload: settings });
  };

  const changePageSettings = (key: string, value: string) => {
    dispatchDraftUpdate({ type: 'change_page_settings', payload: { key, value }});
  };

  const changeCategory = (category: Category) => {
    dispatchDraftUpdate({ type: 'change_category', payload: category });
  };

  const resetActiveRevision = () => {
    dispatchDraftUpdate({ type: 'reset_active_revision' });
  };

  const reconcileSavedPage = (savedPage: Object) => {
    dispatchDraftUpdate({ type: 'replace_draft', payload: savedPage });
  };

  const patchDraftMeta = (updatedAt) => {
    dispatchDraftUpdate({ type: 'patch_draft_meta', payload: { updatedAt } });
  };

  /** Call before an action whose server result should replace local edits. */
  const adoptNextServerPage = (on: boolean = true) => { forceAdoptRef.current = on; };

  const checkpoint = () => dispatchDraftUpdate({ type: 'checkpoint' });
  const cancelCheckpoint = () => dispatchDraftUpdate({ type: 'cancel_checkpoint' });
  const undo = () => dispatchDraftUpdate({ type: 'undo' });
  const redo = () => dispatchDraftUpdate({ type: 'redo' });

  return {
    // Data
    draft,
    activeRevision,

    // Functions
    updateSlot,
    changeVersion,
    changeTemplateSettings,
    changePageSettings,
    changeCategory,
    resetActiveRevision,
    patchDraftMeta,
    reconcileSavedPage,
    undo,
    redo,
    adoptNextServerPage,
    checkpoint,
    cancelCheckpoint,
    canUndo: canUndo(draftState),
    canRedo: canRedo(draftState),
    historyStep: historyStep(draftState),
  };
};

export default useEditorDraft;
